/**
 * What the console says when it takes access away: the Members row action, and what ending one of an
 * organization's entitlements does to its members. Plain functions, so the wording is tested (access.test.ts).
 */
import type { OrganizationEntitlementSummary, OrganizationMemberSummary } from "@allergy123/contracts";

type MembershipStatus = OrganizationMemberSummary["status"];

/** The Members row button's visible text: the status change the save makes, or Save when the status stays. */
export function memberAction(saved: MembershipStatus, draft: MembershipStatus): string {
  if (draft === saved) return "Save";
  return draft === "active" ? "Reinstate" : draft === "suspended" ? "Suspend" : "End membership";
}

/**
 * The same button's accessible name. It starts with the visible text (WCAG 2.5.3, label in name), so a speech
 * user who says "Suspend" reaches it; the member's name tells one row's button from the next.
 */
export const memberActionLabel = (saved: MembershipStatus, draft: MembershipStatus, member: string): string => `${memberAction(saved, draft)} ${member}`;

/** The products the Organizations screen grants and ends. */
export type ConsoleProduct = "learn" | "clinical";
export const PRODUCT_LABELS: Record<ConsoleProduct, string> = { learn: "learning access", clinical: "clinical access" };

/**
 * Another current entitlement the organization holds for the same product, granted here or written by billing.
 * While one remains, ending this one takes nothing from the members. `GET /admin/organizations` lists only the
 * current ones, so anything else in the list still grants the product.
 */
export const otherEntitlementFor = (entitlements: readonly OrganizationEntitlementSummary[], ending: Pick<OrganizationEntitlementSummary, "id" | "product">): OrganizationEntitlementSummary | undefined =>
  entitlements.find((e) => e.id !== ending.id && e.product === ending.product);

const through = (other: OrganizationEntitlementSummary) => (other.source === "stripe" ? "its Stripe subscription, which billing manages" : "another entitlement");

/** What the members lose when the organization's last entitlement for the product ends. */
const LOSS: Record<ConsoleProduct, string> = {
  learn: "Its members lose training through this practice on their next request; a direct learning entitlement is unaffected.",
  clinical: "Its members lose clinical access on their next request; their clinical permissions stay on the memberships.",
};

/** The confirm step's question: what ending this entitlement will do, given what else the organization holds. */
export function endQuestion(product: ConsoleProduct, organizationName: string, other: OrganizationEntitlementSummary | undefined): string {
  const label = PRODUCT_LABELS[product];
  return other
    ? `End this ${label} entitlement for ${organizationName}? It also holds ${label} through ${through(other)}, so its members keep it.`
    : `End ${label} for ${organizationName}? ${LOSS[product]} Granting it again restores access.`;
}

/** The note once the entitlement has ended. */
export function endedNote(product: ConsoleProduct, organizationName: string, other: OrganizationEntitlementSummary | undefined): string {
  const label = PRODUCT_LABELS[product];
  return other
    ? `${organizationName}: that ${label} entitlement ended. The practice still holds ${label} through ${through(other)}, so its members keep it.`
    : `${organizationName}: ${label[0]!.toUpperCase()}${label.slice(1)} ended. ${LOSS[product]}`;
}
